import { useCallback, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import { PopUpIcon, ClipIcon, CloseIcon } from '~components/icons'
import { useDropzone } from 'react-dropzone'
import { COLORS } from '~theme/colors'
import { RichTextEditor } from '~components/forms/rich-text-editor/rich-text-editor'
import type { AttachedFile } from '../../../../types'
import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  fileLinkSx,
  fileItemContainerSx,
  fileDeleteButtonSx,
} from '../../../../styles'

interface RemarkFieldWithAttachProps {
  label: string
  value: string
  onChange: (value: string) => void
  onFilesSelect?: (files: File[]) => void
  onFileSizeError?: (rejectedFiles: File[]) => void
  attachedFiles?: AttachedFile[]
  onRemoveFile?: (fileId: string) => void
  isUploading?: boolean
}

export function RemarkFieldWithAttach({
  label,
  value,
  onChange,
  onFilesSelect,
  onFileSizeError,
  attachedFiles = [],
  onRemoveFile,
  isUploading = false,
}: RemarkFieldWithAttachProps) {
  const [fileToDelete, setFileToDelete] = useState<AttachedFile | null>(null)
  const [isExpandedOpen, setIsExpandedOpen] = useState(false)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: { file: File }[]) => {
      if (acceptedFiles.length > 0 && onFilesSelect) {
        onFilesSelect(acceptedFiles)
      }
      if (fileRejections.length > 0 && onFileSizeError) {
        onFileSizeError(fileRejections.map((r) => r.file))
      }
    },
    [onFilesSelect, onFileSizeError],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: true,
  })

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '1px solid',
        borderColor: isDragActive
          ? COLORS.secondary.midBlue
          : COLORS.greys.light,
        borderStyle: isDragActive ? 'dashed' : 'solid',
        borderRadius: 1,
        p: 1.5,
        minWidth: 0,
        overflow: 'visible',
        backgroundColor: isDragActive ? COLORS.secondary.blueTint : undefined,
        transition: 'border-color 0.2s, background-color 0.2s',
        position: 'relative',
      }}
    >
      {isDragActive && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: COLORS.greys.almostWhite,
            zIndex: 1,
          }}
        >
          <Typography
            variant="paragraph2"
            sx={{ color: COLORS.secondary.midBlue }}
          >
            Drop files here to attach (max {MAX_FILE_SIZE_LABEL} per file)
          </Typography>
        </Box>
      )}
      <input {...getInputProps()} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="label"
          component="label"
          sx={{ color: COLORS.greys.dark }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Expand">
            <IconButton
              size="small"
              onClick={() => setIsExpandedOpen(true)}
              sx={{ p: 0.25 }}
            >
              <PopUpIcon />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClipIcon style={{ fontSize: '0.875rem' }} />}
            onClick={open}
            disabled={isUploading}
            sx={{
              fontSize: '0.75rem',
              py: 0.25,
              px: 1,
              minWidth: 'auto',
              textTransform: 'none',
            }}
          >
            {isUploading ? 'Uploading...' : 'Attach'}
          </Button>
        </Box>
      </Box>
      <RichTextEditor
        value={value}
        onChange={onChange}
        height="120px"
        placeholder="Add remarks..."
      />
      {attachedFiles.length > 0 && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            overflow: 'hidden',
          }}
        >
          {attachedFiles.map((file) => (
            <Box key={file.id} sx={fileItemContainerSx}>
              <Box
                component="a"
                href={file.signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={`${file.name} (${file.fileSize})`}
                sx={{
                  ...fileLinkSx,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {file.name} ({file.fileSize})
              </Box>
              {onRemoveFile && (
                <IconButton
                  size="small"
                  className="file-delete-button"
                  onClick={() => setFileToDelete(file)}
                  sx={fileDeleteButtonSx}
                >
                  <CloseIcon style={{ fontSize: '0.875rem' }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={fileToDelete !== null}
        onClose={() => setFileToDelete(null)}
      >
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{fileToDelete?.name}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFileToDelete(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (fileToDelete && onRemoveFile) {
                onRemoveFile(fileToDelete.id)
              }
              setFileToDelete(null)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isExpandedOpen}
        onClose={() => setIsExpandedOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {label}
          <IconButton
            size="small"
            onClick={() => setIsExpandedOpen(false)}
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RichTextEditor
            value={value}
            onChange={onChange}
            height="400px"
            placeholder="Add remarks..."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIsExpandedOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
